import classNames from 'classnames';
import React from 'react';
import Image from '@/components/atoms/image/image';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import CtaLinkList from '@/components/organisms/cta-link-list/cta-link-list';
import TagList from '@/components/organisms/tag-list/tag-list';
import Lightbox from '@/components/utilities/lightbox/lightbox';
import styles from './country-map-details.module.scss';

interface CountryMapDetailsProps {
  /** The class name to apply to the component */
  className?: string;
  /** The item details to show */
  data?: Offers.Components['CountryMapDestinationCard'];
  /** Whether the lightbox is open or not */
  isOpen?: boolean;
  /** The callback to close the lightbox */
  onClose: () => void;
}

const CountryMapDetails = ({
  className,
  data,
  isOpen,
  onClose,
}: CountryMapDetailsProps) => {
  if (!data) return;

  return (
    <Lightbox isOpen={isOpen} onClose={onClose}>
      <div className={classNames(styles.component, className)}>
        {data.preHeadline && (
          <Typography className={styles.preHeadline} tagStyle="labelMedium">
            {data.preHeadline}
          </Typography>
        )}

        {data.headline && (
          <Typography className={styles.headline} tagStyle="displayMedium">
            {data.headline}
          </Typography>
        )}

        {data.subHeadline && (
          <Typography className={styles.price} tagStyle="headingMedium">
            {data.subHeadline}
          </Typography>
        )}

        {data.tags?.length > 0 && (
          <TagList
            tags={data.tags.map(tag => ({
              text: tag,
              variant: 'secondary',
            }))}
          />
        )}

        {data.image && (
          <div className={styles.imageWrapper}>
            <Image
              alt={data.image.altText}
              aspectRatio="sixteenByNine"
              className={styles.image}
              src={data.image.url}
              srcSet={{
                laptop: {
                  width: 1050,
                },
                tablet: {
                  width: 848,
                },
              }}
              unoptimized
              width={704}
            />
          </div>
        )}

        {data.text && <RichText text={data.text} />}

        {data.facts && data.facts.length > 0 && (
          <div className={styles.list}>
            {data.facts.map((fact, factIndex) => (
              <div key={factIndex} className={styles.listItem}>
                <span>{fact.title}</span>
                <span>
                  {fact.formattedValue
                    ? fact.formattedValue
                    : fact.value
                      ? `${fact.value.value} ${fact.value.unitOfMeasure}`
                      : ''}
                </span>
              </div>
            ))}
          </div>
        )}

        {data.link && (
          <CtaLinkList
            items={[
              {
                $type: 'ButtonLinkCtaDto',
                link: data.link,
                settings: {
                  variant: 'primary',
                },
              },
            ]}
          />
        )}
      </div>
    </Lightbox>
  );
};

CountryMapDetails.displayName = 'CountryMapDetails';

export default CountryMapDetails;
